import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/tc-mobilesms")({
  head: () => ({
    meta: [
      { title: "RESTORE Hair | Mobile SMS Terms" },
      {
        name: "description",
        content:
          "Terms and conditions around our SMS policy. Click here to learn more about how we protect your privacy",
      },
      {
        property: "og:title",
        content: "Mobile Messaging Terms and Conditions — RESTORE® Hair",
      },
      {
        property: "og:description",
        content:
          "Read the RESTORE® Hair SMS/MMS program terms: opt-in, opt-out, message and data rates, and privacy.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "canonical", href: "https://restorehair.com/tc-mobilesms/" },
    ],
  }),
  component: MobileSmsTermsPage,
});

function Block({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <p>
      {label ? <strong className="text-foreground">{label}</strong> : null}
      {children}
    </p>
  );
}

function MobileSmsTermsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-background py-10 md:py-16">
          <div className="mx-auto max-w-3xl px-4">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Mobile Messaging Terms and Conditions
            </h1>

            <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
              <p>
                <strong className="text-foreground">Restore Holdings, LLC.</strong> (“We,”
                “Us,” “Our”) is offering a mobile messaging program (the “Program”), subject
                to these Mobile Messaging Terms and Conditions (the “Terms”). If you do not
                wish to continue participating in the program or no longer agree to these
                Terms, you can reply “<strong className="text-foreground">STOP</strong>” to
                any mobile message from Us in order to opt out of the Program.
              </p>

              <Block label="User Opt In:&nbsp;">
                {" "}
                The Program allows users to receive SMS/MMS mobile messages by users
                affirmatively opting into the Program. Regardless of the opt-in method you
                utilized to join the Program, you agree that these Terms apply to your
                participation in the Program. The mobile messaging service used by Us to
                communicate with you requires human intervention for Our mobile messages to
                be initiated, and thus Our mobile messages are not sent to you by an
                automatic telephone dialing system (“ATDS” or “autodialer”). Nevertheless,
                by participating in the Program,{" "}
                <strong className="text-foreground">
                  you agree to receive autodialed marketing mobile messages and you
                  understand that consent is not required to make any purchase from Us.
                </strong>
              </Block>

              <Block label="Program Description:">
                {" "}
                Without limiting the scope of the Program, users that opt into the Program
                can expect to receive messages concerning promotions and product updates
                from Restore Holdings, LLC.
              </Block>

              <Block label="Cost and Frequency:">
                {" "}
                Message and data rates may apply. The Program involves recurring mobile
                messages, and additional mobile messages may be sent based on your
                interaction with Us.
              </Block>

              <Block label="Our Warranty:">
                {" "}
                We will not be liable for any delays or failures in the receipt of any
                mobile messages connected with this Program. Delivery of mobile messages is
                subject to effective transmission from your wireless service
                provider/network operator, and is outside of Our control. T-Mobile is not
                liable for delayed or undelivered mobile messages.
              </Block>

              <Block label="Term of Use &amp; Privacy Policy:">
                {" "}
                By opting into the Program or otherwise sharing your personal information
                with Us in connection with the Program, you expressly consent to Our Terms
                of Service,{" "}
                <a
                  href="https://restorehair.com/privacy-policy/"
                  className="text-primary underline"
                >
                  https://restorehair.com/privacy-policy/
                </a>
                , and to Our Privacy Policy,{" "}
                <a
                  href="https://restorehair.com/privacy-policy/"
                  className="text-primary underline"
                >
                  https://restorehair.com/privacy-policy/
                </a>
                , including but not limited to, Our use, sharing, exchange and/or disclosure
                of your information as further outlined therein.
              </Block>

              <Block label="Miscellaneous:">
                {" "}
                You warrant and represent to Us that you have all necessary rights, power,
                and authority to agree to these Terms and perform your obligations
                hereunder, and nothing contained in this Agreement or in the performance of
                such obligations will place you in breach of any other contract or
                obligation. The failure of either party to exercise in any respect any right
                provided for herein will not be deemed a waiver of any further rights
                hereunder. If any provision of these Terms is found to be unenforceable or
                invalid, that provision will be limited or eliminated to the minimum extent
                necessary so that this Agreement will otherwise remain in full force and
                effect and enforceable. Any new features, changes, updates or improvements
                of the Program shall be subject to these Terms unless explicitly stated
                otherwise in writing. From time to time, we may update these Terms, and We
                encourage you to periodically check these Terms for updates. Your continued
                participation in the Program affirms your agreement to any changes we make
                to these Terms.
              </Block>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}